import {
  createOrgFunctionRowSchema,
  type ParsedMembershipRow,
  type ParsedOrgFunctionRow
} from '@/lib/excel';
import { renumberRows, zodRowValidator } from '@/lib/draft-rows/draftRow';
import { membershipDuplicateKeys } from './draftRowValidation';
import { fillGapsByKey } from './fillGapsByKey';
import { duplicateKeyFor } from '@/lib/draft-rows/markDuplicateRows';

interface TeamSurface {
  rows: ParsedOrgFunctionRow[];
  memberships: ParsedMembershipRow[];
}

const validateTeamRow = zodRowValidator(createOrgFunctionRowSchema);
const [membershipKey] = membershipDuplicateKeys;

/**
 * Merges the data-sync teams into the surface on screen. A team matches an
 * on-screen row by name only and fills just an empty lead; its department,
 * description and mailbox stay as the admin sees them (the source never sets a
 * team's department). An unmatched team appends, and so does every membership
 * the surface does not hold yet. Returns the current surface unchanged when
 * nothing changed.
 */
export function mergeSetupTeams(current: TeamSurface, imported: TeamSurface): TeamSurface {
  const rows = fillGapsByKey(current.rows, imported.rows, {
    keyOf: (data) => data.name,
    fields: ['leadEmail'],
    validate: validateTeamRow
  });

  const heldMemberships = new Set(
    current.memberships.map((row) => duplicateKeyFor(membershipKey, row.data))
  );
  const appendedMemberships = imported.memberships.filter(
    (row) => !heldMemberships.has(duplicateKeyFor(membershipKey, row.data))
  );

  if (rows === current.rows && appendedMemberships.length === 0) return current;
  return {
    rows,
    memberships:
      appendedMemberships.length > 0
        ? renumberRows([...current.memberships, ...appendedMemberships])
        : current.memberships
  };
}
